import { useEffect, useState } from "react";
import config from "../../config";
import { colors } from "../../style";
import { Btn, Dialog, Icon, ui } from "../ui";

// Meetings are only ever recorded by the desktop and mobile apps; the web app
// shows them once they're uploaded. This explains that.
export function GetTheApps({ compact = false }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: "14px",
      }}
    >
      <span
        style={{
          width: "56px",
          height: "56px",
          borderRadius: "16px",
          background: colors.tealTint,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon name="mic" size={26} color={colors.teal} />
      </span>
      <h2 style={{ margin: 0, fontSize: "20px", color: colors.text }}>
        {compact ? "Record with Sidekick" : "No meetings yet"}
      </h2>
      <p
        style={{
          ...ui.muted,
          margin: 0,
          fontSize: "14px",
          lineHeight: 1.6,
          maxWidth: "360px",
        }}
      >
        Meetings are recorded with the Sidekick app on your computer or phone,
        for in-person meetings and online calls. Once a recording is summarised
        it shows up here too.
      </p>
      <AppLinks />
      <p style={{ ...ui.muted, margin: 0, fontSize: "12px" }}>
        Sign in to the app with the same account you use here.
      </p>
    </div>
  );
}

// Phones and tablets see the mobile app first
const onMobile =
  typeof navigator !== "undefined" &&
  (navigator.userAgentData?.mobile ??
    /Android|iPhone|iPad|Mobile/.test(navigator.userAgent));

const MAC_BUILDS = {
  arm64: { label: "Apple silicon (M1 or later)", other: "x64" },
  x64: { label: "Intel", other: "arm64" },
};

const macDownload = (arch) =>
  `${config.mac_download_base}/Sidekick-latest-${arch}.dmg`;

// Every Mac browser says "Intel" in its user agent, so start with Apple silicon
// (every Mac since 2020) and only switch when Chrome's client hints say x86.
function useMacArch() {
  const [arch, setArch] = useState("arm64");
  useEffect(() => {
    navigator.userAgentData
      ?.getHighEntropyValues?.(["architecture"])
      .then((v) => {
        if (v.platform === "macOS" && v.architecture === "x86") setArch("x64");
      })
      .catch(() => {});
  }, []);
  return arch;
}

function AppLinks() {
  const desktop = <MacDownload key="desktop" />;
  const mobile = (
    <ComingSoon
      key="mobile"
      icon="smartphone"
      label="Sidekick for iPhone and Android"
    />
  );
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: "100%",
        maxWidth: "380px",
      }}
    >
      {onMobile ? [mobile, desktop] : [desktop, mobile]}
    </div>
  );
}

function MacDownload() {
  const arch = useMacArch();
  const { label, other } = MAC_BUILDS[arch];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
      <Btn
        as="a"
        href={macDownload(arch)}
        variant="gradient"
        icon="download"
      >
        Download Sidekick for Mac
      </Btn>
      <span style={{ ...ui.muted, fontSize: "12px" }}>
        {label}.{" "}
        <a href={macDownload(other)} style={{ color: colors.teal }}>
          {MAC_BUILDS[other].label} Mac?
        </a>{" "}
        Windows is coming soon.
      </span>
    </div>
  );
}

function ComingSoon({ icon, label }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        padding: "9px 14px",
        borderRadius: "9px",
        border: `1px dashed ${colors.borderStrong}`,
        fontSize: "13px",
        color: colors.textMuted,
      }}
    >
      <Icon name={icon} size={14} />
      {label}: coming soon
    </span>
  );
}

export function GetTheAppsDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} label="Get the Sidekick apps">
      <div style={{ padding: "28px 24px 24px", position: "relative" }}>
        <Btn
          variant="ghost"
          icon="close"
          aria-label="Close"
          onClick={onClose}
          style={{ position: "absolute", top: "12px", right: "12px" }}
        />
        <GetTheApps compact />
      </div>
    </Dialog>
  );
}
